import { useState, useEffect, useRef, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft,
  ChevronRight,
  Trash2,
  ZoomIn,
  ZoomOut,
  Loader2,
  AlignStartVertical,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignEndHorizontal,
  AlignVerticalSpaceAround,
  Wand2,
  AlertTriangle,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  buildContentSnapTargets,
  buildPageSnapTargets,
  computeSnap,
  quantize,
  alignRects,
  distributeVertically,
  tidyRects,
  EMPTY_CONTENT,
  type ContentSnapTargets,
  type Guide,
  type PageSnapTargets,
  type TextItemLike,
} from "./templateEditor/snapping";

// PDF.js worker — use local bundle via Vite's ?url import (no CDN dependency).
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

// ── Types ───────────────────────────────────────────────────────────
export type PlacedField = {
  name: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
};

export type FieldPositionsConfig = { fields: PlacedField[] };

type Props = {
  pdfBytes: Uint8Array;
  initialFields?: PlacedField[];
  onChange: (fields: PlacedField[]) => void;
  fieldNames?: readonly string[];
  sampleData?: Record<string, string>;
  /**
   * Allow the same field name to be placed in multiple positions (e.g. an
   * invoice number that appears twice). Off by default so contract / owner
   * templates keep their one-placement-per-field behaviour.
   */
  allowDuplicates?: boolean;
  /**
   * Render placed boxes as opaque white with dark text, previewing how a
   * stamped value covers the template text beneath it (invoice templates).
   */
  whiteout?: boolean;
};

// ── Palette ─────────────────────────────────────────────────────────
const FIELD_PALETTE = [
  // Tenant
  "TENANT_FULLNAME",
  "TENANT_EMAIL",
  "TENANT_PHONE_NUMBER",
  // Landlord / representative
  "OWNER_NAME",
  "REPRESENTOR_NAME",
  // Property address
  "PROPERTY_STREET_NAME",
  "PROPERTY_STREET_NUMBER",
  "PROPERTY_ZIP_CODE",
  "PROPERTY_CITY_NAME",
  "ROOM_NAME_NUMBER",
  // Lease dates & amounts
  "LEASE_START_DATE",
  "LEASE_END_DATE",
  "RENT_AMOUNT",
  "DEPOSIT_AMOUNT",
  // Bank details
  "ACCOUNT_NAME",
  "IBAN",
  "BIC",
  "BANK_NAME",
  // Signing — landlord side then tenant side (SIGNING_DATE intentionally appears
  // twice in the contract so we expose two draggable fields with identical values)
  "REPRESENTOR_CITY",
  "SIGNING_DATE",
  "TENANT_SIGNING_CITY",
  "TENANT_SIGNING_DATE",
  // Signature image stamps — not text
  "REPRESENTOR_SIGNATURE",
  "TENANT_SIGNATURE",
] as const;

const SAMPLE: Record<string, string> = {
  TENANT_FULLNAME: "Max Mustermann",
  TENANT_EMAIL: "max@example.com",
  TENANT_PHONE_NUMBER: "+49 170 1234567",
  OWNER_NAME: "Coliviq GmbH",
  REPRESENTOR_NAME: "John Doe",
  PROPERTY_STREET_NAME: "Musterstraße",
  PROPERTY_STREET_NUMBER: "42",
  PROPERTY_ZIP_CODE: "10115",
  PROPERTY_CITY_NAME: "Berlin",
  ROOM_NAME_NUMBER: "Room A",
  LEASE_START_DATE: "01.05.2026",
  LEASE_END_DATE: "31.10.2026",
  RENT_AMOUNT: "650.00",
  DEPOSIT_AMOUNT: "975.00",
  ACCOUNT_NAME: "Coliviq GmbH",
  IBAN: "DE89 3704 0044 0532 0130 00",
  BIC: "COBADEFFXXX",
  BANK_NAME: "Commerzbank",
  REPRESENTOR_CITY: "München",
  SIGNING_DATE: "15.04.2026",
  TENANT_SIGNING_CITY: "Berlin",
  TENANT_SIGNING_DATE: "15.04.2026",
  REPRESENTOR_SIGNATURE: "[Landlord sig]",
  TENANT_SIGNATURE: "[Tenant sig]",
};

const COLORS: Record<string, string> = {
  TENANT_FULLNAME: "#3b82f6",
  TENANT_EMAIL: "#3b82f6",
  TENANT_PHONE_NUMBER: "#3b82f6",
  OWNER_NAME: "#8b5cf6",
  REPRESENTOR_NAME: "#8b5cf6",
  PROPERTY_STREET_NAME: "#10b981",
  PROPERTY_STREET_NUMBER: "#10b981",
  PROPERTY_ZIP_CODE: "#10b981",
  PROPERTY_CITY_NAME: "#10b981",
  ROOM_NAME_NUMBER: "#10b981",
  LEASE_START_DATE: "#f59e0b",
  LEASE_END_DATE: "#f59e0b",
  RENT_AMOUNT: "#ef4444",
  DEPOSIT_AMOUNT: "#ef4444",
  ACCOUNT_NAME: "#06b6d4",
  IBAN: "#06b6d4",
  BIC: "#06b6d4",
  BANK_NAME: "#06b6d4",
  REPRESENTOR_CITY: "#8b5cf6",
  SIGNING_DATE: "#f59e0b",
  TENANT_SIGNING_CITY: "#3b82f6",
  TENANT_SIGNING_DATE: "#f59e0b",
  REPRESENTOR_SIGNATURE: "#a855f7",
  TENANT_SIGNATURE: "#ec4899",
};

const DEFAULT_W = 180;
const DEFAULT_H = 16;
const DEFAULT_FS = 10;
const MIN_W = 20;
const MIN_H = 10;

// Snap feel: constant in screen pixels regardless of zoom.
const SNAP_PX = 6;

// The server renderer (pdf-lib) draws text in Noto Sans with the BASELINE at
// the field's y (its bottom edge). The preview mirrors that: the sample label
// is baseline-anchored on the box bottom using Noto Sans' hhea metrics
// (ascent 1069 / descent 293 per 1000 upm). With line-height = asc + desc the
// line box has zero half-leading, so the glyph baseline sits exactly
// ascent-em below the span top.
const NOTO_ASC = 1.069;
const NOTO_DESC = 0.293;
const PREVIEW_FONT = '"Noto Sans", sans-serif';

const GUIDE_COLORS: Record<Guide["source"], string> = {
  field: "#3b82f6", // blue — aligned to another field
  content: "#e11d48", // red — aligned to the template's own text
};

// LEASOR_SIGNATURE is the pre-rename name of REPRESENTOR_SIGNATURE — still
// recognised so a template saved before the rename previews correctly.
const isSignatureField = (name: string) =>
  name === "TENANT_SIGNATURE" ||
  name === "REPRESENTOR_SIGNATURE" ||
  name === "LEASOR_SIGNATURE";

// Measure sample text width in PDF points (canvas px at scale 1 == pt).
let measureCtx: CanvasRenderingContext2D | null = null;
function sampleTextWidth(text: string, fontSize: number): number {
  if (!measureCtx)
    measureCtx = document.createElement("canvas").getContext("2d");
  if (!measureCtx) return 0;
  measureCtx.font = `${fontSize}px ${PREVIEW_FONT}`;
  return measureCtx.measureText(text).width;
}

// ── Component ───────────────────────────────────────────────────────
export default function TemplateFieldEditor({
  pdfBytes,
  initialFields,
  onChange,
  fieldNames,
  sampleData,
  allowDuplicates = false,
  whiteout = false,
}: Props) {
  const { t } = useLanguage();
  const palette = fieldNames ?? FIELD_PALETTE;
  const sample = sampleData ?? SAMPLE;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(0);
  const [pgW, setPgW] = useState(0);
  const [pgH, setPgH] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [fields, setFields] = useState<PlacedField[]>(initialFields ?? []);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draggingNew, setDraggingNew] = useState<string | null>(null);
  // Interaction state is keyed by index into `fields`, not by name —
  // allowDuplicates (invoice templates) lets one name occupy several spots.
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [resizeIdx, setResizeIdx] = useState<number | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [guides, setGuides] = useState<Guide[]>([]);
  // Bumped when a page's content snap targets finish loading (drives the
  // "no text layer" badge; drags started before that just snap to fields).
  const [, setContentTick] = useState(0);
  const [previewFontReady, setPreviewFontReady] = useState(false);

  const offset = useRef({ x: 0, y: 0 });
  const resizeAnchor = useRef({ topY: 0, leftX: 0 });
  const snapTargetsRef = useRef<PageSnapTargets | null>(null);
  const contentRef = useRef<Map<number, ContentSnapTargets>>(new Map());

  // Re-render once the preview font is available so baseline/width previews
  // use real Noto Sans metrics instead of the fallback font's.
  useEffect(() => {
    let dead = false;
    document.fonts
      ?.load(`10px ${PREVIEW_FONT}`)
      .then(() => {
        if (!dead) setPreviewFontReady(true);
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, []);

  // ── Load PDF doc ──
  useEffect(() => {
    let dead = false;
    setLoading(true);
    setLoadError(null);
    contentRef.current = new Map();
    const data = pdfBytes.slice(); // defensive copy
    console.log("[TemplateFieldEditor] Loading PDF, bytes:", data.byteLength);
    pdfjsLib
      .getDocument({ data })
      .promise.then((doc) => {
        if (dead) return;
        console.log("[TemplateFieldEditor] PDF loaded, pages:", doc.numPages);
        setPdfDoc(doc);
        setPages(doc.numPages);
        setPage(0);
        setLoading(false);
      })
      .catch((err) => {
        if (dead) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error("[TemplateFieldEditor] PDF load error:", msg);
        setLoadError(msg);
        setLoading(false);
      });
    return () => {
      dead = true;
    };
  }, [pdfBytes]);

  // ── Render page + extract content snap targets ──
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let dead = false;
    pdfDoc.getPage(page + 1).then((pg) => {
      if (dead) return;
      const vp = pg.getViewport({ scale });
      const vp1 = pg.getViewport({ scale: 1 });
      const canvas = canvasRef.current!;
      canvas.width = vp.width;
      canvas.height = vp.height;
      setPgW(vp1.width);
      setPgH(vp1.height);
      void pg.render({ canvasContext: canvas.getContext("2d")!, viewport: vp })
        .promise;

      if (!contentRef.current.has(page)) {
        const rotation = ((pg.rotate % 360) + 360) % 360;
        if (rotation !== 0) {
          // Rotated pages: text-space baselines aren't horizontal editor
          // lines. Fall back to field-only snapping.
          contentRef.current.set(page, EMPTY_CONTENT);
          setContentTick((t) => t + 1);
        } else {
          pg.getTextContent()
            .then((tc) => {
              if (dead) return;
              // CropBox offset: map through the scale-1 viewport so targets
              // match what the canvas displays.
              const offsetView = pg.view[0] !== 0 || pg.view[1] !== 0;
              const toEditor = offsetView
                ? (x: number, y: number) => {
                    const [vx, vy] = vp1.convertToViewportPoint(x, y) as [
                      number,
                      number,
                    ];
                    return { x: vx, y: vp1.height - vy };
                  }
                : undefined;
              const items: TextItemLike[] = [];
              for (const it of tc.items) {
                const cand = it as {
                  str?: unknown;
                  transform?: unknown;
                  width?: unknown;
                };
                if (
                  typeof cand.str === "string" &&
                  Array.isArray(cand.transform) &&
                  typeof cand.width === "number"
                ) {
                  items.push({
                    str: cand.str,
                    transform: cand.transform as number[],
                    width: cand.width,
                  });
                }
              }
              contentRef.current.set(
                page,
                buildContentSnapTargets(items, toEditor)
              );
              setContentTick((t) => t + 1);
            })
            .catch(() => {
              if (dead) return;
              contentRef.current.set(page, EMPTY_CONTENT);
              setContentTick((t) => t + 1);
            });
        }
      }
    });
    return () => {
      dead = true;
    };
  }, [pdfDoc, page, scale]);

  // notify parent
  useEffect(() => {
    onChange(fields);
  }, [fields, onChange]);

  // ── Coord helpers ──
  const toScreen = useCallback(
    (f: PlacedField) => ({
      left: f.x * scale,
      top: (pgH - f.y - f.height) * scale,
      width: f.width * scale,
      height: f.height * scale,
    }),
    [scale, pgH]
  );

  const toPdf = useCallback(
    (sx: number, sy: number, sh: number) => ({
      x: sx / scale,
      y: pgH - sy / scale - sh / scale,
    }),
    [scale, pgH]
  );

  const pageContent = () => contentRef.current.get(page) ?? EMPTY_CONTENT;

  const buildTargetsExcluding = useCallback(
    (exclude: { idx?: number | null; name?: string | null }) =>
      buildPageSnapTargets(
        fields.filter(
          (f, i) =>
            f.page === page &&
            i !== exclude.idx &&
            (exclude.name == null || f.name !== exclude.name)
        ),
        contentRef.current.get(page) ?? EMPTY_CONTENT
      ),
    [fields, page]
  );

  const updateFieldAt = (idx: number, patch: Partial<PlacedField>) => {
    setFields((prev) =>
      prev.map((f, i) => (i === idx ? quantize({ ...f, ...patch }) : f))
    );
  };

  // ── Drop new field ──
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const name = e.dataTransfer.getData("field-name");
    if (!name) return;
    const r = overlayRef.current!.getBoundingClientRect();
    // Signature is an image stamp — larger default box than a text field
    const isSig = isSignatureField(name);
    const w = isSig ? 160 : DEFAULT_W;
    const h = isSig ? 48 : DEFAULT_H;
    const fs = isSig ? 8 : DEFAULT_FS;
    let { x, y } = toPdf(e.clientX - r.left, e.clientY - r.top, h * scale);
    if (!e.altKey) {
      // In allowDuplicates mode existing placements of the same name stay
      // valid snap targets (aligning the second copy to the first is the
      // point); otherwise the old placement is being replaced, so skip it.
      const snapped = computeSnap(
        { x, y, width: w, height: h },
        buildTargetsExcluding({ name: allowDuplicates ? null : name }),
        SNAP_PX / scale
      );
      x = snapped.x;
      y = snapped.y;
    }
    const placed = quantize({
      name,
      page,
      x,
      y,
      width: w,
      height: h,
      fontSize: fs,
    });
    // allowDuplicates: append (same field can sit in several spots).
    // Otherwise keep the one-placement-per-name rule (replace).
    const next = allowDuplicates
      ? [...fields, placed]
      : [...fields.filter((f) => f.name !== name), placed];
    setFields(next);
    setSelected([next.length - 1]);
    setDraggingNew(null);
    containerRef.current?.focus({ preventScroll: true });
  }

  // ── Drag / resize existing field ──
  function onFieldDown(e: React.MouseEvent, idx: number) {
    e.preventDefault();
    e.stopPropagation();
    containerRef.current?.focus({ preventScroll: true });
    if (e.shiftKey) {
      // Shift-click toggles membership in the multi-selection (no drag).
      setSelected((prev) =>
        prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
      );
      return;
    }
    setSelected((prev) => (prev.includes(idx) ? prev : [idx]));
    const f = fields[idx];
    if (!f) return;
    const r = overlayRef.current!.getBoundingClientRect();
    const s = toScreen(f);
    offset.current = {
      x: e.clientX - r.left - s.left,
      y: e.clientY - r.top - s.top,
    };
    snapTargetsRef.current = buildTargetsExcluding({ idx });
    setDragIdx(idx);
  }

  function onResizeDown(e: React.MouseEvent, idx: number) {
    e.preventDefault();
    e.stopPropagation();
    containerRef.current?.focus({ preventScroll: true });
    const f = fields[idx];
    if (!f) return;
    resizeAnchor.current = { topY: f.y + f.height, leftX: f.x };
    setSelected([idx]);
    setResizeIdx(idx);
  }

  function onMove(e: React.MouseEvent) {
    const r = overlayRef.current?.getBoundingClientRect();
    if (!r) return;

    if (resizeIdx !== null) {
      const sx = e.clientX - r.left;
      const sy = e.clientY - r.top;
      const { topY, leftX } = resizeAnchor.current;
      const width = Math.max(MIN_W, sx / scale - leftX);
      const bottom = pgH - sy / scale;
      const height = Math.max(MIN_H, topY - bottom);
      // Keep the TOP edge anchored while dragging the bottom-right handle.
      setFields((prev) =>
        prev.map((f, i) =>
          i === resizeIdx ? { ...f, width, height, y: topY - height } : f
        )
      );
      return;
    }

    if (dragIdx === null) return;
    const f = fields[dragIdx];
    if (!f) return;
    const sx = e.clientX - r.left - offset.current.x;
    const sy = e.clientY - r.top - offset.current.y;
    let { x, y } = toPdf(sx, sy, f.height * scale);
    if (!e.altKey && snapTargetsRef.current) {
      const snapped = computeSnap(
        { x, y, width: f.width, height: f.height },
        snapTargetsRef.current,
        SNAP_PX / scale
      );
      x = snapped.x;
      y = snapped.y;
      setGuides(snapped.guides);
    } else {
      setGuides([]);
    }
    setFields((prev) =>
      prev.map((fld, i) => (i === dragIdx ? { ...fld, x, y } : fld))
    );
  }

  function onUp() {
    const commit = dragIdx ?? resizeIdx;
    if (commit !== null) {
      setFields((prev) => prev.map((f, i) => (i === commit ? quantize(f) : f)));
    }
    setDragIdx(null);
    setResizeIdx(null);
    setGuides([]);
    snapTargetsRef.current = null;
  }

  // ── Keyboard: nudge, delete, escape ──
  function onKeyDown(e: React.KeyboardEvent) {
    if (selected.length === 0) return;
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;

    if (e.key === "Escape") {
      setSelected([]);
      return;
    }
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      setFields((prev) => prev.filter((_, i) => !selected.includes(i)));
      setSelected([]);
      return;
    }
    const step = e.shiftKey ? 10 : 1;
    let dx = 0;
    let dy = 0;
    if (e.key === "ArrowLeft") dx = -step;
    else if (e.key === "ArrowRight") dx = step;
    else if (e.key === "ArrowUp")
      dy = step; // bottom-left origin: up = +y
    else if (e.key === "ArrowDown") dy = -step;
    else return;
    e.preventDefault();
    setFields((prev) =>
      prev.map((f, i) =>
        selected.includes(i) && f.page === page
          ? quantize({ ...f, x: f.x + dx, y: f.y + dy })
          : f
      )
    );
  }

  // ── Alignment tools ──
  type IndexedField = PlacedField & { _i: number };

  const indexedFields: IndexedField[] = fields.map((f, i) => ({
    ...f,
    _i: i,
  }));

  const selectedOnPage = indexedFields.filter(
    (f) => f.page === page && selected.includes(f._i)
  );

  /** Write transformed rects back into `fields` by index, stripping `_i`. */
  function commitIndexed(changed: IndexedField[]) {
    const byIdx = new Map(changed.map((f) => [f._i, f]));
    setFields((prev) =>
      prev.map((f, i) => {
        const c = byIdx.get(i);
        if (!c) return f;
        const { _i, ...rest } = c;
        void _i;
        return rest;
      })
    );
  }

  function applyToSelection(
    transform: (rects: IndexedField[]) => IndexedField[]
  ) {
    commitIndexed(transform(selectedOnPage).map(quantize));
  }

  function tidyPage() {
    const onThisPage = indexedFields.filter((f) => f.page === page);
    commitIndexed(tidyRects(onThisPage, 3).map(quantize));
  }

  function removeAt(idx: number) {
    setFields((prev) => prev.filter((_, i) => i !== idx));
    // Later indices shift down by one after the removal.
    setSelected((prev) =>
      prev.filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i))
    );
  }

  const placedSet = new Set(fields.map((f) => f.name));
  const onPage = indexedFields.filter((f) => f.page === page);
  const singleSelected =
    selected.length === 1 ? (fields[selected[0]] ?? null) : null;
  const singleSelectedIdx = selected.length === 1 ? selected[0] : null;
  const content = pageContent();
  const noTextLayer = contentRef.current.has(page) && !content.hasTextLayer;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">
          {t("admin.templateFieldEditor.loadingPdf", "Loading PDF...")}
        </span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-destructive font-medium">
          {t("admin.templateFieldEditor.loadError", "Failed to load PDF")}
        </p>
        <p className="text-xs text-muted-foreground mt-1 max-w-md">
          {loadError}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* ── Palette ── */}
      <div className="rounded-lg border bg-muted/30 p-3">
        <p className="text-[11px] font-medium text-muted-foreground mb-2">
          {t(
            "admin.templateFieldEditor.paletteHint",
            "Drag fields onto the PDF — they snap to the template's own lines and to other fields (hold Alt to place freely):"
          )}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {palette.map((name) => {
            const done = !allowDuplicates && placedSet.has(name);
            const count = fields.filter((f) => f.name === name).length;
            const c = COLORS[name] ?? "#6b7280";
            return (
              <div
                key={name}
                draggable={!done}
                onDragStart={(e) => {
                  e.dataTransfer.setData("field-name", name);
                  e.dataTransfer.effectAllowed = "copy";
                  setDraggingNew(name);
                }}
                onDragEnd={() => setDraggingNew(null)}
                className={`text-[11px] font-mono px-2 py-0.5 rounded border select-none transition-all
                  ${done ? "opacity-30 cursor-default line-through" : "cursor-grab hover:shadow-md active:cursor-grabbing"}
                  ${draggingNew === name ? "ring-2 ring-offset-1" : ""}`}
                style={{ borderColor: c, color: c, backgroundColor: `${c}10` }}
              >
                {name}
                {allowDuplicates && count > 0 && (
                  <span className="ml-1 opacity-60">×{count}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Nav + Align tools + Zoom ── */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            disabled={page <= 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground w-16 text-center">
            {t("admin.templateFieldEditor.pageOf", "Page {current} / {total}")
              .replace("{current}", String(page + 1))
              .replace("{total}", String(pages))}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            disabled={page >= pages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          {noTextLayer && (
            <span className="text-[10px] text-muted-foreground border rounded px-1.5 py-0.5">
              {t(
                "admin.templateFieldEditor.noTextLayer",
                "No text layer — snapping to fields only"
              )}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {selectedOnPage.length >= 2 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={t(
                  "admin.templateFieldEditor.alignLeft",
                  "Align left edges"
                )}
                onClick={() => applyToSelection((r) => alignRects(r, "left"))}
              >
                <AlignStartVertical className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={t(
                  "admin.templateFieldEditor.alignRight",
                  "Align right edges"
                )}
                onClick={() => applyToSelection((r) => alignRects(r, "right"))}
              >
                <AlignEndVertical className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={t(
                  "admin.templateFieldEditor.alignTop",
                  "Align top edges"
                )}
                onClick={() => applyToSelection((r) => alignRects(r, "top"))}
              >
                <AlignStartHorizontal className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={t(
                  "admin.templateFieldEditor.alignBaselines",
                  "Align baselines"
                )}
                onClick={() =>
                  applyToSelection((r) => alignRects(r, "baseline"))
                }
              >
                <AlignEndHorizontal className="w-4 h-4" />
              </Button>
              {selectedOnPage.length >= 3 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title={t(
                    "admin.templateFieldEditor.distributeVertically",
                    "Distribute vertically (equal baseline spacing)"
                  )}
                  onClick={() => applyToSelection(distributeVertically)}
                >
                  <AlignVerticalSpaceAround className="w-4 h-4" />
                </Button>
              )}
              <div className="w-px h-4 bg-border mx-1" />
            </>
          )}
          {onPage.length >= 2 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              title={t(
                "admin.templateFieldEditor.tidyUpTooltip",
                "Unify nearly-equal columns and baselines on this page"
              )}
              onClick={tidyPage}
            >
              <Wand2 className="w-3.5 h-3.5" />
              {t("admin.templateFieldEditor.tidyUp", "Tidy up")}
            </Button>
          )}
          <div className="w-px h-4 bg-border mx-1" />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale((s) => Math.max(0.5, s - 0.2))}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground w-10 text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale((s) => Math.min(2.5, s + 0.2))}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ── Canvas + Overlay ── */}
      <div
        ref={containerRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="relative border rounded-lg overflow-auto bg-gray-100 outline-none focus-visible:ring-1 focus-visible:ring-ring"
        style={{ maxHeight: "520px" }}
      >
        <div className="relative inline-block">
          <canvas ref={canvasRef} className="block" />
          <div
            ref={overlayRef}
            className="absolute inset-0"
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
            }}
            onDrop={onDrop}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onMouseDown={(e) => {
              // Click on empty canvas clears the selection.
              if (e.target === overlayRef.current) setSelected([]);
            }}
          >
            {/* Snap guide lines */}
            {guides.map((g, i) =>
              g.axis === "v" ? (
                <div
                  key={`g${i}`}
                  className="absolute pointer-events-none"
                  style={{
                    left: g.at * scale,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    backgroundColor: GUIDE_COLORS[g.source],
                  }}
                />
              ) : (
                <div
                  key={`g${i}`}
                  className="absolute pointer-events-none"
                  style={{
                    top: (pgH - g.at) * scale,
                    left: 0,
                    right: 0,
                    height: 1,
                    backgroundColor: GUIDE_COLORS[g.source],
                  }}
                />
              )
            )}

            {onPage.map((f) => {
              const pos = toScreen(f);
              const c = COLORS[f.name] ?? "#6b7280";
              const isSig = isSignatureField(f.name);
              const isSelected = selected.includes(f._i);
              const text = sample[f.name] ?? f.name;
              const overflows =
                !isSig &&
                previewFontReady &&
                sampleTextWidth(text, f.fontSize) > f.width;
              return (
                <div
                  key={f._i}
                  data-field-name={f.name}
                  className="absolute group"
                  style={{
                    left: pos.left,
                    top: pos.top,
                    width: pos.width,
                    height: pos.height,
                    cursor: dragIdx === f._i ? "grabbing" : "grab",
                  }}
                  onMouseDown={(e) => onFieldDown(e, f._i)}
                >
                  <div
                    className="w-full h-full rounded-sm"
                    style={{
                      border: `${isSelected ? 2 : 1.5}px ${isSelected ? "solid" : "dashed"} ${c}`,
                      // whiteout: opaque white box previews how a stamped
                      // value covers the template text beneath it (invoices).
                      backgroundColor: whiteout ? "#ffffff" : `${c}18`,
                      boxShadow: isSelected ? `0 0 0 1px ${c}55` : undefined,
                    }}
                  >
                    {isSig && (
                      <div className="w-full h-full flex items-center justify-center overflow-hidden">
                        <span
                          className="text-[10px] font-mono leading-none truncate px-1"
                          style={{ color: whiteout ? "#1f2933" : c }}
                        >
                          {text}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Positioned against the outer (borderless) box so the
                      preview baseline is exactly the pdf-lib baseline */}
                  {!isSig && (
                    <>
                      {/* Baseline tick — pdf-lib draws the text baseline
                          exactly on the box's bottom edge */}
                      <div
                        className="absolute pointer-events-none"
                        style={{
                          left: -2,
                          right: -2,
                          bottom: 0,
                          borderBottom: `1px solid ${c}99`,
                        }}
                      />
                      <span
                        className="absolute pointer-events-none whitespace-nowrap"
                        style={{
                          left: 0,
                          top: (f.height - NOTO_ASC * f.fontSize) * scale,
                          fontFamily: PREVIEW_FONT,
                          fontSize: f.fontSize * scale,
                          lineHeight: NOTO_ASC + NOTO_DESC,
                          color: whiteout ? "#1f2933" : c,
                        }}
                      >
                        {text}
                      </span>
                    </>
                  )}
                  <div
                    className="absolute -top-4 left-0 text-[9px] font-mono px-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
                    style={{ backgroundColor: c, color: "white" }}
                  >
                    {f.name}
                  </div>
                  {overflows && (
                    <div
                      className="absolute -top-1.5 left-1/2 -translate-x-1/2"
                      title={t(
                        "admin.templateFieldEditor.overflowWarning",
                        "Sample text is wider than the field — the PDF will wrap it to a second line. Widen the field or reduce the font size."
                      )}
                    >
                      <AlertTriangle className="w-3 h-3 text-amber-500 fill-amber-100" />
                    </div>
                  )}
                  <button
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeAt(f._i);
                    }}
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                  {isSelected && (
                    <div
                      className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-white border rounded-[2px]"
                      style={{ borderColor: c, cursor: "nwse-resize" }}
                      onMouseDown={(e) => onResizeDown(e, f._i)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Properties panel ── */}
      {singleSelected && (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2 flex-wrap">
          <span
            className="text-[11px] font-mono font-medium"
            style={{ color: COLORS[singleSelected.name] ?? "#6b7280" }}
          >
            {singleSelected.name}
          </span>
          {(
            [
              ["X", "x", 0.5, undefined],
              ["Y", "y", 0.5, undefined],
              ["W", "width", 0.5, MIN_W],
              ["H", "height", 0.5, MIN_H],
            ] as const
          ).map(([label, key, step, min]) => (
            <label
              key={key}
              className="flex items-center gap-1 text-[11px] text-muted-foreground"
            >
              {label}
              <input
                type="number"
                step={step}
                value={singleSelected[key]}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (Number.isNaN(v) || singleSelectedIdx === null) return;
                  updateFieldAt(singleSelectedIdx, {
                    [key]: min != null ? Math.max(min, v) : v,
                  });
                }}
                className="w-16 h-6 rounded border bg-background px-1 text-[11px] tabular-nums"
              />
            </label>
          ))}
          {!isSignatureField(singleSelected.name) && (
            <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
              {t("admin.templateFieldEditor.font", "Font")}
              <input
                type="number"
                step={0.5}
                min={6}
                max={24}
                value={singleSelected.fontSize}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (Number.isNaN(v) || singleSelectedIdx === null) return;
                  updateFieldAt(singleSelectedIdx, {
                    fontSize: Math.min(24, Math.max(6, v)),
                  });
                }}
                className="w-14 h-6 rounded border bg-background px-1 text-[11px] tabular-nums"
              />
            </label>
          )}
          <span className="text-[10px] text-muted-foreground ml-auto">
            {t(
              "admin.templateFieldEditor.unitsHint",
              "pt · arrows nudge 1pt, Shift 10pt · width = wrap limit in the PDF"
            )}
          </span>
        </div>
      )}

      {/* ── Summary badges ── */}
      {fields.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {fields.map((f, i) => (
              <Badge
                key={i}
                variant="outline"
                className="text-[10px] gap-1 cursor-pointer hover:bg-muted"
                style={{
                  borderColor: COLORS[f.name] ?? "#6b7280",
                  color: COLORS[f.name] ?? "#6b7280",
                }}
                onClick={() => {
                  setPage(f.page);
                  setSelected([i]);
                }}
              >
                {f.name} <span className="opacity-50">p{f.page + 1}</span>
              </Badge>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-destructive shrink-0"
            onClick={() => {
              setFields([]);
              setSelected([]);
            }}
          >
            {t("admin.templateFieldEditor.clearAll", "Clear all")}
          </Button>
        </div>
      )}
    </div>
  );
}
